// @flow
// $FlowFixMe
import React, { Fragment, useEffect, useMemo, useState } from "react";
import { get, noop } from "lodash";
import { useRequested } from "@tvg/top-pools";
import type { RaceCardBettingInterest, RaceCardRunner } from "@tvg/types/Race";
import { events as AlchemerEvents } from "@urp/alchemer";
import RunnerInfoTemplate from "@fdr/atomic-ui/_templates/RunnerInfo";
import mediator from "@tvg/mediator";
import { connect } from "react-redux";
import { graphql } from "@apollo/client/react/hoc";
import IPP from "@fdr/ipp";
import type { HelpTypeEnum } from "@fdr/types/ProgramPage";
import {
  setComponentStatus,
  shouldShowOnboarding
} from "@fdr/utils/onboardingUtils";
import { shouldUpdatePicks } from "@fdr/shared-actions/ProgramPageActions";
import RunnerStatsQuery from "./graphql/queries/RunnerStatsQuery.graphql";
import ApolloOptions from "./graphql/options.graqh";
import type { Props } from "./types";

export const RunnerInfo = (props: Props) => {
  const {
    runner,
    runnerId,
    race,
    wagerProfile,
    runnerStats,
    pastRaces,
    numberOfRaces,
    dispatch,
    isLoading,
    isLeg,
    isHelpModalOpen,
    helpModalType,
    openHelpModal,
    isPastPerformanceEnabled,
    fcpClient,
    behgClient,
    isLogged,
    bettingInterests,
    betLeg,
    hasWebSockets,
    refetch,
    pastPerformanceError,
    useOldPercentageRating,
    useGraphStatusV2,
    shouldShowFirstOnboarding,
    isAllRunnersOpen,
    updatePicks,
    onTooltipClick,
    isNumberFireSection,
    numColumns,
    showRunnerUpdate,
    isWagerable,
    showWinProb,
    showPastRaces
  } = props;

  const [ippOpen, setIppOpen] = useState(false);
  const [entityRunnerId, setEntityRunnerId] = useState("");
  const [runnerName, setRunnerName] = useState("");
  const [
    shouldShowFirstOnboardingRunnerRating,
    setShouldShowFirstOnboardingRunnerRating
  ] = useState(shouldShowOnboarding("runner-rating"));
  const hasRequested = useRequested(isLoading);

  const saveRunner = () => {
    if (runner && get(runner, "runners.length") > 0) {
      const selectedRunner = get(runner, "runners").find(
        (biRunner) => biRunner.runnerId === runnerId
      );

      if (selectedRunner) {
        setRunnerName(get(selectedRunner, "horseName"));
        setEntityRunnerId(get(selectedRunner, "entityRunnerId"));
      }
    }
  };
  // $FlowFixMe
  const bettingInterest = bettingInterests[betLeg]?.find(
    (item) => item.runners[0].runnerId === runnerId
  );

  const setOnboardingStatus = (componentName: string, action: string) => {
    setComponentStatus(componentName, action);
    const newOnboardingState = shouldShowOnboarding("runner-rating");

    if (shouldShowFirstOnboardingRunnerRating !== newOnboardingState)
      setShouldShowFirstOnboardingRunnerRating(newOnboardingState);
  };

  const openModal = (text: HelpTypeEnum, isNotApplicable: boolean) => {
    openHelpModal(text, isNotApplicable);
    setOnboardingStatus("runner-rating", "click");
  };

  useEffect(() => {
    saveRunner();

    if (
      shouldShowFirstOnboarding &&
      shouldShowFirstOnboardingRunnerRating &&
      !isNumberFireSection
    )
      setOnboardingStatus("runner-rating", "seen");
  }, []);

  const togglePastPerformances = () => {
    if (!ippOpen) {
      mediator.base.dispatch({
        type: "PAST_PERFORMANCES:CLICK_MORE_PAGE"
      });

      saveRunner();
      AlchemerEvents.selectMoreButton();
    }

    if (!isLogged) {
      mediator.base.dispatch({
        type: "OPEN_LOGIN",
        payload: {
          callback: () => {
            setIppOpen(!ippOpen);
          }
        }
      });
    } else {
      setIppOpen(!ippOpen);
    }
  };

  useEffect(() => {
    if (hasRequested && !!runnerStats && !!pastRaces && !updatePicks) {
      dispatch(shouldUpdatePicks(true));
    }
  }, [
    hasRequested,
    updatePicks,
    JSON.stringify(runnerStats),
    JSON.stringify(pastRaces)
  ]);

  const getIPPRunners = (): RaceCardRunner[] =>
    // $FlowFixMe
    bettingInterests[betLeg]?.reduce((acc, bi: RaceCardBettingInterest) => {
      const runners = bi.runners
        .map((biRunner: RaceCardRunner): RaceCardRunner => biRunner)
        .filter((biRunner) => biRunner.scratched !== true);
      return acc.concat(runners);
    }, []) ?? [];

  const onRunnerClick = (entRunnerId: string, name: string) => {
    mediator.base.dispatch({
      type: "PAST_PERFORMANCES:SELECT_PP_MORE_PAGE",
      payload: {
        runnerName: name,
        trackName: get(race, "track.trackName")
      }
    });
    setEntityRunnerId(entRunnerId);
    setRunnerName(name);
  };

  return useMemo(
    () => (
      <Fragment>
        <RunnerInfoTemplate
          openHelpModal={openModal}
          runnerStats={runnerStats}
          runner={runner}
          pastRaces={pastRaces}
          numberOfRaces={numberOfRaces}
          isLoadingPP={!hasRequested}
          isLeg={isLeg}
          showIPP={isPastPerformanceEnabled && showPastRaces}
          openPastPerformances={togglePastPerformances}
          refetch={refetch}
          pastPerformanceError={pastPerformanceError}
          useOldPercentageRating={useOldPercentageRating}
          useGraphStatusV2={useGraphStatusV2}
          shouldShowFirstOnboarding={
            shouldShowFirstOnboarding && shouldShowFirstOnboardingRunnerRating
          }
          onTooltipClick={onTooltipClick}
          setOnboardingStatus={setOnboardingStatus}
          isAllRunnersOpen={isAllRunnersOpen}
          numColumns={numColumns}
          isNumberFireSection={isNumberFireSection}
          bettingInterest={bettingInterest}
          showRunnerUpdate={showRunnerUpdate}
          isWagerable={isWagerable}
          showWinProb={showWinProb}
        />
        {isPastPerformanceEnabled && (
          <IPP
            onRunnerClick={onRunnerClick}
            runners={getIPPRunners()}
            runnerName={runnerName}
            entityRunnerId={entityRunnerId}
            originRaceCode={get(race, "track.trackCode", "")}
            originRaceNumber={get(race, "raceNumber", "")}
            originRaceConditions={{
              distance: get(race, "distance"),
              surface: get(race, "surface.shortName")
            }}
            hasWebSockets={hasWebSockets}
            originRace={race}
            wagerProfile={wagerProfile}
            fcpClient={fcpClient}
            behgClient={behgClient}
            device="mobile"
            onModalClose={togglePastPerformances}
            isModalOpen={ippOpen}
          />
        )}
      </Fragment>
    ),
    [
      isHelpModalOpen,
      helpModalType,
      hasRequested,
      ippOpen,
      isLogged,
      isLeg,
      isPastPerformanceEnabled,
      betLeg,
      runnerName,
      entityRunnerId,
      isAllRunnersOpen,
      updatePicks,
      shouldShowFirstOnboarding,
      shouldShowFirstOnboardingRunnerRating,
      JSON.stringify(bettingInterests),
      JSON.stringify(runner),
      JSON.stringify(race),
      JSON.stringify(runnerStats),
      JSON.stringify(pastRaces),
      showWinProb,
      numColumns,
      showPastRaces
    ]
  );
};

RunnerInfo.defaultProps = {
  dispatch: noop,
  runnerStats: null,
  runner: null,
  isLogged: false,
  pastRaces: null,
  fcpClient: null,
  race: null,
  runnerId: "0",
  wagerProfile: "FDR-Generic",
  isLoading: false,
  isLeg: false,
  openHelpModal: noop,
  helpModalType: "PWR RATING",
  isHelpModalOpen: false,
  isPastPerformanceEnabled: true,
  bettingInterests: [],
  betLeg: 0,
  hasWebSockets: false,
  refetch: noop,
  pastPerformanceError: "",
  useOldPercentageRating: false,
  useGraphStatusV2: false,
  shouldShowFirstOnboarding: false,
  isAllRunnersOpen: false,
  updatePicks: false,
  onTooltipClick: noop,
  isNumberFireSection: false,
  numColumns: 1,
  showRunnerUpdate: false,
  isWagerable: true,
  showWinProb: true,
  showPastRaces: true
};

export default connect(
  (store) => ({
    isPastPerformanceEnabled: get(
      store,
      "capi.featureToggles.isPastPerformanceEnabled",
      true
    ),
    useOldPercentageRating: get(
      store,
      "capi.featureToggles.useOldPercentageRating"
    ),
    pastPerformanceError: get(store, "capi.messages.pastPerformanceError", ""),
    isLogged: get(store, "userData.logged", false),
    isLeg: get(store, "RaceProgram.isAllRunnersOpen.length", 1) > 1,
    updatePicks: get(store, "RaceProgram.updatePicks", false),
    useGraphStatusV2: get(store, "capi.featureToggles.graphStatusV2", false)
  }),
  (dispatch) => ({ dispatch })
)(graphql(RunnerStatsQuery, ApolloOptions)(RunnerInfo));
